import { useFieldWiring } from "./Field";
import { cn } from "@/lib/cn";
import { forwardRef, type ComponentPropsWithoutRef } from "react";

const THUMB = [
  "[&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none",
  "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-card",
  "[&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow-raised",
  "[&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full",
  "[&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-card [&::-moz-range-thumb]:bg-primary",
  "[&::-moz-range-thumb]:shadow-raised",
];

export type SliderProps = Omit<
  ComponentPropsWithoutRef<"input">,
  "type" | "value" | "defaultValue" | "onChange" | "min" | "max" | "step"
> & {
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Accessible name when the slider is not inside a `Field`. */
  label?: string;
  /** Spoken value (`aria-valuetext`), e.g. "120 pontos". */
  formatValue?: (value: number) => string;
};

/** Styled native range input: a filled primary track and a raised thumb on the palette tokens. */
export const Slider = forwardRef<HTMLInputElement, SliderProps>(function Slider(
  { value, onValueChange, min = 0, max = 100, step = 1, label, formatValue, className, style, ...rest },
  ref,
) {
  const { invalid: _invalid, ...wiring } = useFieldWiring({
    id: rest.id,
    "aria-describedby": rest["aria-describedby"],
  });
  const span = max - min;
  const fill = span > 0 ? ((Math.min(Math.max(value, min), max) - min) / span) * 100 : 0;

  return (
    <input
      {...rest}
      {...wiring}
      ref={ref}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      aria-label={rest["aria-label"] ?? label}
      aria-valuetext={formatValue?.(value)}
      onChange={(event) => onValueChange(Number(event.target.value))}
      style={{ ...style, backgroundSize: `${fill}% 100%` }}
      className={cn(
        "h-2 w-full cursor-pointer appearance-none rounded-full accent-primary outline-none",
        "bg-card-text/10 bg-gradient-to-r from-primary to-primary bg-no-repeat",
        "focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        THUMB,
        className,
      )}
    />
  );
});
